// ===== 出荷編集 (備考のみ) / 出荷新規 =====

import { useEffect, useState } from "react";
import { Alert, Button, Paper, Stack, TextField, Typography } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { PageContext } from "../PageContext";
import Time from "../../../util/Time";
import { InfoList, InfoListColDef } from "../../../components/InfoList";
import { idForDisplay } from "../../../util/uuidTextConverter";
import { userInfoCol } from "../../../util/userDisplay";
import { DetailGuard, useDetailFetch, useDetailRetry } from "../../../components/DetailFetch";
import { carryOutRow } from "../../../schema/console/carry_out";

export function CarryOutEditPage({ pageContext }: { pageContext: PageContext }) {
  const api = pageContext.api;
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [remarks, setRemarks] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [retryCount, retry] = useDetailRetry();
  const state = useDetailFetch<carryOutRow>(() => {
    if (!id) {
      return Promise.resolve(null);
    }
    return api.carry_out.getFromId({ id });
  }, [api, id, retryCount]);

  useEffect(() => {
    if (state.status === "ready") {
      setRemarks(state.data.remarks ?? "");
    }
  }, [state]);

  const submit = async (carryOutId: string) => {
    setSubmitting(true);
    try {
      await api.carry_out.patch({ id: carryOutId, remarks: remarks.trim() || null });
      navigate(`/console/carry-out/${carryOutId}/`);
    } catch {
      setError("保存に失敗しました。もう一度お試しください");
      setSubmitting(false);
    }
  };

  return (
    <DetailGuard state={state} notFoundLabel={`出荷 ${id}`} onRetry={retry}>
      {(co) => (
        <CarryOutEditForm co={co} remarks={remarks} setRemarks={setRemarks} error={error} submitting={submitting} onSubmit={() => submit(co.id)} />
      )}
    </DetailGuard>
  );
}

function CarryOutEditForm({ co, remarks, setRemarks, error, submitting, onSubmit }: {
  co: carryOutRow; remarks: string; setRemarks: (value: string) => void; error: string | null; submitting: boolean; onSubmit: () => void;
}) {
  const infoCols: InfoListColDef[] = [
    { label: "ID", context: idForDisplay(co.id) },
    { label: "登録日", context: new Time(co.createdAt).Format("YYYY/MM/dd HH:mm:ss") },
    userInfoCol(co.User),
  ];

  return (
    <Stack p={3} gap={2} maxWidth={900} mx="auto">
      <Typography variant="h4" fontWeight="bold">出荷情報の編集</Typography>
      <InfoList cols={infoCols} columns={3} />
      <Paper variant="outlined" sx={{ p: 3 }}>
        <Stack gap={2} maxWidth={600} mx="auto">
          <Alert severity="info" sx={{ py: 0.5 }}>
            ロット情報（材料・LotNo・容量）と QR スキャンは登録後の詳細画面から行います。出荷日は配下ロットの貼付が全数完了したタイミングで自動確定します。
          </Alert>
          <TextField label="備考" multiline minRows={2} value={remarks} onChange={(e) => setRemarks(e.target.value)} fullWidth />
          {error && <Alert severity="error">{error}</Alert>}
          <Stack alignItems="center">
            <Button variant="contained" size="large" disabled={submitting} onClick={onSubmit} sx={{ py: 1.5, width: "60%" }}>更新</Button>
          </Stack>
        </Stack>
      </Paper>
    </Stack>
  );
}

export function CarryOutNewPage({ pageContext }: { pageContext: PageContext }) {
  const api = pageContext.api;
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    setSubmitting(true);
    try {
      const res = await api.carry_out.put({});
      navigate(`/console/carry-out/${res.id}/`);
    } catch {
      setError("出荷の作成に失敗しました。もう一度お試しください");
      setSubmitting(false);
    }
  };

  return (
    <Stack p={3} gap={2} maxWidth={900} mx="auto">
      <Typography variant="h4" fontWeight="bold">出荷情報</Typography>
      <Paper variant="outlined" sx={{ p: 3 }}>
        <Stack gap={2} maxWidth={600} mx="auto" alignItems="center">
          <Typography color="text.secondary">出荷を作成し、続けてロットを追加します。</Typography>
          {error && <Alert severity="error" sx={{ width: "100%" }}>{error}</Alert>}
          <Button variant="contained" size="large" disabled={submitting} onClick={submit} sx={{ py: 1.5, width: "60%" }}>出荷を作成</Button>
        </Stack>
      </Paper>
    </Stack>
  );
}
